
import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { batchUpdateManager } from '@/utils/realtimeOptimization';

// ⚡ АК-47: Экспортируем типы для использования в пропсах
export type Room = Tables<'rooms'>;
type RoomInsert = TablesInsert<'rooms'>;
type RoomUpdate = TablesUpdate<'rooms'>;

interface UseRoomsOptions {
  page?: number;
  pageSize?: number;
  searchQuery?: string;
}

export const useRooms = (options: UseRoomsOptions = {}) => {
  const { page = 1, pageSize = 10000, searchQuery } = options;
  const [rooms, setRooms] = useState<Room[]>([]);
  const [totalRooms, setTotalRooms] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { currentHotel, hotelReady } = useOptimizedHotel();

  // Stabilize hotel ID to prevent unnecessary re-renders
  const stableHotelId = useMemo(() => currentHotel?.id, [currentHotel?.id]);

  const fetchRooms = useCallback(async () => {
    if (!hotelReady || !stableHotelId) {
      console.log('🏨 useRooms: Hotel context not ready yet');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🏨 useRooms: Fetching rooms via RPC for hotel:', currentHotel?.name, stableHotelId, 'page:', page);

      // ⚡ АК-47: JSONB-туннель — серверная пагинация, поиск, сортировка
      const { data, error: rpcError } = await supabase.rpc('get_rooms_paginated', {
        p_hotel_id: stableHotelId,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_search: searchQuery || null,
      });

      if (rpcError) {
        console.error('🚫 RPC error:', rpcError);
        throw rpcError;
      }

      const result = data as any;
      const roomsData = (result?.rooms || []) as Room[];
      const total = result?.total || 0;

      console.log('📊 Rooms RPC result:', {
        pageRooms: roomsData.length,
        total,
        page,
        pageSize,
      });

      setRooms(roomsData);
      setTotalRooms(total);
      setError(null);
      console.log('✅ useRooms: Loaded', roomsData.length, 'of', total, 'rooms for hotel:', currentHotel?.name);
    } catch (error: any) {
      console.error('💥 Error fetching rooms:', error);
      
      let errorMessage = 'Не удалось загрузить номера';
      if (error.code === '42501') {
        errorMessage = 'Недостаточно прав для просмотра номеров';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast({
        title: "Ошибка",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [stableHotelId, currentHotel?.name, toast, hotelReady, page, pageSize, searchQuery]);

  const createRoom = async (room: Omit<RoomInsert, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      console.log('🏨 createRoom: Starting process...');

      const { data, error } = await supabase
        .from('rooms')
        .insert(room)
        .select()
        .single();

      if (error) {
        console.error('🚫 Database insert error:', error);
        if (error.code === '42501') {
          throw new Error('Недостаточно прав для создания номера.');
        } else if (error.code === '23505') {
          throw new Error('Номер с такими данными уже существует.');
        } else {
          throw new Error(`Ошибка базы данных: ${error.message}`);
        }
      }

      if (!data) {
        throw new Error('Номер не был создан - нет данных от сервера');
      }

      console.log('✅ Room created successfully:', data.id);
      
      // Refetch to get correct pagination state
      await fetchRooms();
      
      toast({
        title: "Успешно",
        description: `Номер "${room.name}" создан`,
      });

      return data;
    } catch (error: any) {
      console.error('🚫 createRoom error:', error);
      throw error;
    }
  };

  const updateRoom = async (id: string, updates: RoomUpdate) => {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setRooms(prev => prev.map(room => 
        room.id === id ? data : room
      ));

      toast({
        title: "Успешно",
        description: "Номер обновлен",
      });

      return data;
    } catch (error: any) {
      console.error('Error updating room:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить номер",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteRoom = async (id: string) => {
    try {
      const { error } = await supabase
        .from('rooms')
        .update({ 
          deleted_at: new Date().toISOString(),
          deleted_by: (await supabase.auth.getUser()).data.user?.id || null
        })
        .eq('id', id);

      if (error) throw error;

      setRooms(prev => prev.filter(room => room.id !== id));
      setTotalRooms(prev => prev - 1);
      
      toast({
        title: "Успешно",
        description: "Номер удален",
      });
    } catch (error: any) {
      console.error('Error deleting room:', error);
      
      let errorMessage = "Не удалось удалить номер";
      if (error.code === '23503') {
        errorMessage = "Номер используется в бронированиях и не может быть удален";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Ошибка",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Fetch rooms when hotel is ready and stable
  useEffect(() => {
    if (hotelReady) {
      fetchRooms();
    }
  }, [hotelReady, fetchRooms]);

  // Real-time subscriptions with batching
  useEffect(() => {
    if (!hotelReady || !stableHotelId) return;

    console.log('🏨 Setting up real-time subscription for rooms');
    
    const channel = supabase
      .channel(`rooms-changes-${stableHotelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'rooms',
          filter: `hotel_id=eq.${stableHotelId}`
        },
        (payload) => {
          console.log('🏨 Real-time INSERT:', payload.new);
          
          batchUpdateManager.addUpdate(
            `room_insert_${stableHotelId}`,
            payload,
            (updates) => {
              const newRooms = updates.map(u => u.new as Room);
              setRooms(prev => {
                const existingIds = new Set(prev.map(r => r.id));
                const uniqueNew = newRooms.filter(r => !existingIds.has(r.id));
                return [...uniqueNew, ...prev];
              });
              setTotalRooms(prev => prev + updates.length);
            },
            { maxBatchSize: 8, batchTimeoutMs: 250, priority: 'medium' }
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `hotel_id=eq.${stableHotelId}`
        },
        (payload) => {
          console.log('🏨 Real-time UPDATE:', payload.new);
          
          batchUpdateManager.addUpdate(
            `room_update_${stableHotelId}`,
            payload,
            (updates) => {
              const updatedRooms = updates.map(u => u.new as Room);
              setRooms(prev => {
                const updatesMap = new Map(updatedRooms.map(r => [r.id, r]));
                return prev.map(room => updatesMap.get(room.id) || room);
              });
            },
            { maxBatchSize: 10, batchTimeoutMs: 300, priority: 'medium' }
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'rooms',
          filter: `hotel_id=eq.${stableHotelId}`
        },
        (payload) => {
          console.log('🏨 Real-time DELETE:', payload.old);
          
          batchUpdateManager.addUpdate(
            `room_delete_${stableHotelId}`,
            payload,
            (updates) => {
              const deletedIds = new Set(updates.map(u => (u.old as Room).id));
              setRooms(prev => prev.filter(room => !deletedIds.has(room.id)));
              setTotalRooms(prev => Math.max(0, prev - updates.length));
            },
            { maxBatchSize: 5, batchTimeoutMs: 150, priority: 'low' }
          );
        }
      )
      .subscribe();

    return () => {
      console.log('🏨 Cleaning up real-time subscription for rooms');
      supabase.removeChannel(channel);
    };
  }, [hotelReady, stableHotelId]);

  const totalPages = Math.ceil(totalRooms / pageSize);

  return {
    rooms,
    loading,
    error,
    totalRooms,
    totalPages,
    createRoom,
    updateRoom,
    deleteRoom,
    refetch: fetchRooms,
  };
};
